import { useCallback, useLayoutEffect, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';
import { BREAKPOINTS, mediaQuery } from '@/config/breakpoints';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { useResizeWidth } from '@/lib/useResizeWidth';
import type { CardSize } from './catalogPrefs';

/**
 * Число колонок макета по ширине окна: мобильный ≤760, узкий ≤1100, обычный,
 * широкий ≥1500, ультраширокий ≥1900 (README «Grid»).
 */
const COLUMNS: Record<CardSize, readonly [number, number, number, number, number]> = {
  s: [3, 4, 6, 8, 10],
  m: [2, 3, 4, 5, 6],
  l: [2, 3, 3, 4, 5],
};

/** Поля страницы (2 × `--app-page-x`) и запас на системный скроллбар. */
const PAGE_CHROME_PX = 80;

/**
 * Самая узкая сетка, под которую макет даёт число колонок шага: нижняя граница
 * окна минус обвязка страницы. Мобильный шаг не считается — панель там на весь экран.
 */
const DESIGN_WIDTH: readonly number[] = [
  0,
  BREAKPOINTS.mobile - PAGE_CHROME_PX,
  BREAKPOINTS.narrowGrid - PAGE_CHROME_PX,
  BREAKPOINTS.wide - PAGE_CHROME_PX,
  BREAKPOINTS.ultra - PAGE_CHROME_PX,
];

/**
 * Карточка может стать у́же самой узкой по макету не больше чем на десятую часть —
 * дальше сетка теряет колонку. Без допуска пограничная ширина (окно 1024 с панелью)
 * давала бы одну колонку в полэкрана вместо двух обычных.
 */
const CARD_SHRINK = 0.9;

/**
 * Сетке тесно (открыта панель разбора) — она теряет колонки, а не плющит
 * карточки: колонок остаётся столько, чтобы карточка была не у́же, чем в самой
 * узкой сетке макета для этого числа колонок (с допуском `CARD_SHRINK`).
 * Места хватает — ровно `design`.
 */
export function fitColumns(
  design: number,
  width: number,
  designWidth: number,
  gap: number,
): number {
  if (width >= designWidth) return design;
  const narrowestCard = (designWidth - (design - 1) * gap) / design;
  const fit = Math.floor((width + gap) / (narrowestCard * CARD_SHRINK + gap));
  return Math.max(1, Math.min(design, fit));
}

/**
 * Колонки сетки каталога — один источник для Grid и Natural. `onReflow` зовётся
 * после каждой смены ширины сетки, когда колонки уже переложены.
 */
export function useGridColumns(
  gridRef: RefObject<HTMLElement | null>,
  size: CardSize,
  onReflow?: () => void,
): number {
  const isMobile = useMediaQuery(mediaQuery.mobile);
  const isNarrow = useMediaQuery(mediaQuery.narrowGrid);
  const isWide = useMediaQuery(mediaQuery.wide);
  const isUltra = useMediaQuery(mediaQuery.ultra);
  const step = isUltra ? 4 : isWide ? 3 : isMobile ? 0 : isNarrow ? 1 : 2;
  const design = COLUMNS[size][step] ?? COLUMNS[size][2]!;
  const [columns, setColumns] = useState(design);

  const measure = useCallback((): number => {
    const grid = gridRef.current;
    if (!grid || step === 0) return design;
    // Зазор — из CSS (`--grid-gap` зависит от размера карточек), зеркала в JS нет.
    const gap = Number.parseFloat(getComputedStyle(grid).columnGap) || 0;
    return fitColumns(design, grid.getBoundingClientRect().width, DESIGN_WIDTH[step] ?? 0, gap);
  }, [gridRef, design, step]);

  // Размер карточек и ширина окна меняют колонки до отрисовки.
  useLayoutEffect(() => {
    setColumns(measure());
  }, [measure]);

  // Ширина сетки: flushSync перекладывает колонки в том же кадре — без кадра со сплющенными карточками.
  useResizeWidth(gridRef, () => {
    const next = measure();
    if (next !== columns) flushSync(() => setColumns(next));
    onReflow?.();
  });

  return columns;
}
